import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { activityLogsMockData } from '@/lib/constants';
import { useNavigate } from 'react-router-dom';
import TicketModal from 'components/tickets/TicketModal';
import formatToMDY from 'utilities/FormatAsIsoLocal';

interface ActivityLog {
  timestamp: string;
  user: string;
  userType: string;
  activity: string;
  description: string;
  status: string;
  email?: string;
  ipAddress?: string;
  deviceInfo?: string;
  location?: string;
  organization: string;
}

interface ActivityLogsProps {
  filters: {
    userName: string;
    userType: string;
    activity: string;
    status: string;
    startDate: Date | null;
    endDate: Date | null;
    search: string;
    organization: string;
    statuses: string[];
  }
  setFilters: (filters: any) => void;
}

export default function ActivityLogs({ filters, setFilters }: ActivityLogsProps) {
  const [expandedLog, setExpandedLog] = useState<number | null>(null);
  const navigate = useNavigate();
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [selectedLogForTicket, setSelectedLogForTicket] = useState<ActivityLog | null>(null);

  const filteredLogs = useMemo(() => {
    let filtered = [...activityLogsMockData] as ActivityLog[];

    if (filters.statuses && filters.statuses.length > 0) {
      filtered = filtered.filter(log =>
        filters.statuses.includes(log.status.toLowerCase())
      );
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(log =>
        log.user.toLowerCase().includes(searchTerm) ||
        log.userType.toLowerCase().includes(searchTerm) ||
        log.activity.toLowerCase().includes(searchTerm) ||
        log.description.toLowerCase().includes(searchTerm) ||
        log.status.toLowerCase().includes(searchTerm) ||
        log.timestamp.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.userName && !filtered.some(log =>
      log.user.toLowerCase().includes(filters.userName.toLowerCase())
    )) {
      filtered = filtered.filter(log =>
        log.user.toLowerCase().includes(filters.userName.toLowerCase())
      );
    }

    if (filters.activity && filtered.some(log =>
      log.activity.toLowerCase().includes(filters.activity.toLowerCase()))
    ) {
      filtered = filtered.filter(log => log.activity.toLowerCase().includes(filters.activity.toLowerCase()));
    }


    if (filters.userType && filtered.some(log =>
      log.userType.toLowerCase().includes(filters.userType.toLowerCase())
    )) {
      filtered = filtered.filter(log =>
        log.userType.toLowerCase().includes(filters.userType.toLowerCase())
      );
    }

    if (filters.organization) {
      filtered = filtered.filter((email => email.organization.toLowerCase().includes(filters.organization.toLowerCase())));
    }

    if (filters.startDate && filters.endDate) {
      filtered = filtered.filter((activity) => {
        const activityDate = new Date(activity.timestamp);
        // Set time to start of day for start date and end of day for end date
        const startDate = new Date(filters.startDate as Date);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(filters.endDate as Date);
        endDate.setHours(23, 59, 59, 999);

        return activityDate >= startDate && activityDate <= endDate;
      });
    }

    return filtered;
  }, [activityLogsMockData, filters]);

  const DetailRow = ({ label, value, className = '' }: { label: string; value: string; className?: string }) => (
    <div className={`flex border-b border-white/[0.08] py-3 ${className}`}>
      <div className="w-1/4 text-gray-400">{label}</div>
      <div className="w-2/4 text-white break-words whitespace-pre-wrap">{value}</div>
    </div>
  );

  const handleReport = (log: ActivityLog) => {
    setSelectedLogForTicket(log);
    setIsTicketModalOpen(true);
  };

  return (
    <>

      <div className="sticky top-[130px] bg-black z-10 pt-4">
        <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
              <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
                <th className="text-left p-4">Date</th>
                <th className="text-left p-4">Organization</th>
                <th className="text-left p-4">User</th>
                <th className="text-left p-4">Activity</th>
                <th className="text-left p-4">Description</th>
                <th className="text-left p-4">Status</th>
              </tr>
            </thead>
          </table>
        </div>
      </div>

      <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
        <table className="w-full table-fixed">
          <tbody>
            {filteredLogs.map((log, index) => (
              <React.Fragment key={index}>
                <tr
                  className="border-b border-white/[0.08] hover:bg-[#3a3a3a33] cursor-pointer"
                  onClick={() => setExpandedLog(expandedLog === index ? null : index)}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {expandedLog === index ? (
                        <FiChevronDown className="text-gray-400" />
                      ) : (
                        <FiChevronRight className="text-gray-400" />
                      )}
                      <span className="text-gray-400">{formatToMDY(log.timestamp)}</span>
                    </div>
                  </td>
                  <td className="p-4 text-gray-400">{log.organization}</td>
                  <td className="p-4">
                    <span className="text-white">{log.user}</span>
                  </td>
                  <td className="p-4 text-gray-400">{log.activity}</td>
                  <td className="p-4 text-gray-400 break-words whitespace-pre-wrap">{log.description}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${log.status === 'Success' ? 'bg-[#1C3829] text-[#4ADE80]' :
                        log.status === 'Failed' ? 'bg-[#e11d486b] text-red-500' :
                          'bg-yellow-900/30 text-yellow-500'
                      }`}>
                      {log.status}
                    </span>
                  </td>
                </tr>

                {/* Expanded Details View */}
                {expandedLog === index && (
                  <tr>
                    <td colSpan={6} className="bg-[#1a1a1a] border-b border-gray-800/50">
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="text-lg text-white font-medium">Activity Details</h3>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReport(log);
                            }}
                            className="flex items-center gap-2 bg-[#2A2A2A] rounded-md px-4 py-2 text-white hover:bg-[#3a3a3a33]"
                          >
                            Report
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-8">
                          <div className="space-y-3">
                            <DetailRow label="Timestamp" value={new Date(log.timestamp).toLocaleDateString()} />
                            <DetailRow label="User" value={log.user} />
                            <DetailRow label="Email" value={log?.email || 'N/A'} />
                            <DetailRow label="Role" value={log.userType} />
                            <DetailRow label="Organization" value={log.organization} />
                          </div>
                          <div className="space-y-3">
                            <DetailRow label="IP address" value={log?.ipAddress || '192.158.1.38'} />
                            <DetailRow label="Device Information" value={log?.deviceInfo || 'Google Chrome, Windows'} />
                            <DetailRow label="Location" value={log?.location || 'N/A'} />
                            <DetailRow label="Activity" value={log.activity} />
                          </div>
                        </div>
                        <div className="mt-6 grid grid-cols-2 gap-8">
                          <div className="space-y-3">
                            <div className="flex border-b border-white/[0.08] py-3">
                              <div className="w-1/4 text-gray-400">Description</div>
                              <div className="w-3/4 text-white whitespace-pre-wrap">{log.description}</div>
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div className="flex border-b border-white/[0.08] py-3">
                              <div className="w-1/4 text-gray-400">Status</div>
                              <div className="w-3/4">
                                <span className={`px-2 py-1 rounded-full text-xs ${log.status === 'Success' ? 'bg-[#1C3829] text-[#4ADE80]' :
                                    log.status === 'Failed' ? 'bg-[#e11d486b] text-red-500' :
                                      'bg-yellow-900/30 text-yellow-500'
                                  }`}>
                                  {log.status}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <TicketModal
        isOpen={isTicketModalOpen}
        onClose={() => {
          setIsTicketModalOpen(false);
          setSelectedLogForTicket(null);
        }}
        mode="create"
        initialData={selectedLogForTicket ? {
          id: '',
          status: 'open',
          customer: selectedLogForTicket.user,
          name: `Report: ${selectedLogForTicket.activity}`,
          description: `Reporting activity:\n\nTimestamp: ${selectedLogForTicket.timestamp}\nUser: ${selectedLogForTicket.user}\nActivity: ${selectedLogForTicket.activity}\nDescription: ${selectedLogForTicket.description}`,
          addedOn: new Date().toISOString(),
          assignedTo: '',
          lastUpdated: new Date().toISOString(),
          email: selectedLogForTicket.email
        } : null}
      />
    </>
  );
} 